/**
 * src/observability/globalErrorHandlers.ts
 *
 * Captura global de excecoes nao tratadas.
 * Instala window.onerror e window.onunhandledrejection uma unica vez no boot.
 *
 * Regra: nunca lancar. Qualquer erro interno e logado via console.error e ignorado.
 */

import { recordEvent } from './events'

// ─── Dedupe ─────────────────────────────────────────────────────────────────

const DEDUPE_WINDOW_MS = 30_000
const DEDUPE_MAP_MAX = 100

/** Map<key, timestampMs> — escopo de modulo, persiste durante a sessao. */
const dedupeMap = new Map<string, number>()

/**
 * Retorna true se o evento ja foi registrado nos ultimos DEDUPE_WINDOW_MS.
 * Registra (ou atualiza) o timestamp da key no map.
 * Faz cleanup do map quando ultrapassa DEDUPE_MAP_MAX entradas.
 */
function isDuplicate(key: string): boolean {
  const now = Date.now()
  const last = dedupeMap.get(key)

  if (last !== undefined && now - last < DEDUPE_WINDOW_MS) {
    return true
  }

  // Registra / atualiza antes de checar o cap
  dedupeMap.set(key, now)

  // Protecao contra leak quando todas as keys sao unicas
  if (dedupeMap.size > DEDUPE_MAP_MAX) {
    const entries = Array.from(dedupeMap.entries())
    const toDelete = entries.filter(([, ts]) => now - ts > DEDUPE_WINDOW_MS).map(([k]) => k)
    if (toDelete.length > 0) {
      toDelete.forEach(k => dedupeMap.delete(k))
    } else {
      // Todas recentes — apaga a mais antiga
      const sorted = entries.sort((a, b) => a[1] - b[1])
      if (sorted.length > 0) dedupeMap.delete(sorted[0][0])
    }
  }

  return false
}

// ─── Filtros ─────────────────────────────────────────────────────────────────

const NETWORK_NOISE_PATTERNS = [
  'Failed to fetch',
  'NetworkError when attempting to fetch',
  'Network request failed',
  'TIMEOUT',
  'The operation was aborted',
  'AbortError',
]

const CHUNK_PATTERNS = [
  'Loading chunk',
  'ChunkLoadError',
  'Loading CSS chunk',
]

// Erros externos de vendors que não são acionáveis.
// `formid` / `selectIfTextField` vêm do screen-set do Gigya (jQuery + Safari/WebKit):
// o script de registro Gigya usa variáveis jQuery internas (`formid`) que não existem
// no contexto WebKit. O erro persiste com jQuery 3.5.1 e é filtrado aqui porque não
// é acionável pela equipe ProGerar (ver também public/index.html).
const EXTERNAL_VENDOR_PATTERNS = [
  'formid',
]

/**
 * Retorna true quando o evento deve ser descartado silenciosamente.
 * @param message - event.message (onerror) ou String(reason) (unhandledrejection)
 * @param source  - event.filename para onerror; undefined para rejections
 */
function shouldFilter(message: string, source?: string): boolean {
  // 1. Gigya cross-origin sem crossorigin attr — mensagem inutil
  if (message === 'Script error.' && (source === '' || source == null)) {
    return true
  }

  // 2. ResizeObserver false-positive do Chrome
  if (message.includes('ResizeObserver loop')) {
    return true
  }

  // 3. Ruido de rede — offline-first mede saude da rede via sync status
  const msgLower = message.toLowerCase()
  for (const pattern of NETWORK_NOISE_PATTERNS) {
    if (msgLower.includes(pattern.toLowerCase())) {
      return true
    }
  }

  // 4. Chunk load errors — LazyErrorBoundary ja cobre rotas privadas;
  //    rotas publicas ficam para Fase 2 (root boundary)
  for (const pattern of CHUNK_PATTERNS) {
    if (message.includes(pattern)) {
      return true
    }
  }

  // 5. Erros externos de vendors (Gigya screen-set, jQuery/WebKit)
  for (const pattern of EXTERNAL_VENDOR_PATTERNS) {
    if (message.includes(pattern)) {
      return true
    }
  }

  return false
}

// ─── Handlers ────────────────────────────────────────────────────────────────

function onWindowError(event: ErrorEvent): void {
  try {
    const message = event.message ?? ''
    const source = event.filename ?? ''

    if (shouldFilter(message, source)) return

    const key = `window_error|${(message).slice(0, 200)}`
    if (isDuplicate(key)) return

    void recordEvent({
      level: 'error',
      category: 'outro',
      action: 'window_error',
      payload: {
        message,
        source,
        lineno: event.lineno,
        colno: event.colno,
        stack: (event.error as Error | null)?.stack?.slice(0, 500),
      },
    })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[globalErrorHandlers] onWindowError internal error', err)
  }
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  try {
    const reason = event.reason
    const message = reason instanceof Error
      ? reason.message
      : String(reason ?? 'unknown rejection')

    if (shouldFilter(message)) return

    const key = `unhandled_rejection|${message.slice(0, 200)}`
    if (isDuplicate(key)) return

    void recordEvent({
      level: 'error',
      category: 'outro',
      action: 'unhandled_rejection',
      payload: {
        reason: String(reason),
        message: reason instanceof Error ? reason.message : undefined,
        stack: reason instanceof Error ? reason.stack?.slice(0, 500) : undefined,
      },
    })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[globalErrorHandlers] onUnhandledRejection internal error', err)
  }
}

// ─── Install ─────────────────────────────────────────────────────────────────

let installed = false

/**
 * Instala window.onerror e window.onunhandledrejection.
 * Idempotente: segunda chamada e no-op.
 * Guard de SSR: retorna imediatamente se window nao existe.
 * Wrap em try/catch: falha nao propaga para o boot.
 */
export function installGlobalErrorHandlers(): void {
  try {
    if (typeof window === 'undefined') return
    if (installed) return

    window.addEventListener('error', onWindowError)
    window.addEventListener('unhandledrejection', onUnhandledRejection)

    installed = true
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[globalErrorHandlers] install failed', err)
  }
}
